import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const CORNERS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

function cornerPosition(corner) {
  switch (corner) {
    case 'top-left':     return { top: 16, left: 16,  bottom: 'auto', right: 'auto' };
    case 'top-right':    return { top: 16, right: 16, bottom: 'auto', left: 'auto' };
    case 'bottom-left':  return { bottom: 16, left: 16, top: 'auto', right: 'auto' };
    case 'bottom-right':
    default:             return { bottom: 16, right: 16, top: 'auto', left: 'auto' };
  }
}

/**
 * Draggable self-view container. Snaps to the nearest corner on drop so
 * the self-view never lands over the remote participant's face.
 *
 * Double-click the PiP to cycle corners manually. Close button hides it
 * entirely (user can re-show via the parent control).
 */
export default function DraggablePiP({ children, visible, onHide, defaultCorner = 'bottom-right' }) {
  const [corner, setCorner] = useState(defaultCorner);
  const containerRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, offsetX: 0, offsetY: 0 });
  const [floating, setFloating] = useState(null); // { left, top } while mid-drag

  const snapFromPoint = useCallback((x, y) => {
    if (typeof window === 'undefined') return defaultCorner;
    const midX = window.innerWidth / 2;
    const midY = window.innerHeight / 2;
    if (x < midX && y < midY) return 'top-left';
    if (x >= midX && y < midY) return 'top-right';
    if (x < midX && y >= midY) return 'bottom-left';
    return 'bottom-right';
  }, [defaultCorner]);

  const onPointerDown = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top
    };
    setFloating({ left: rect.left, top: rect.top });
    el.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    setFloating({
      left: e.clientX - dragRef.current.offsetX,
      top: e.clientY - dragRef.current.offsetY
    });
  };

  const onPointerUp = (e) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    const next = snapFromPoint(e.clientX, e.clientY);
    setCorner(next);
    setFloating(null);
  };

  const handleDoubleClick = () => {
    const idx = CORNERS.indexOf(corner);
    setCorner(CORNERS[(idx + 1) % CORNERS.length]);
  };

  useEffect(() => {
    if (!visible) setFloating(null);
  }, [visible]);

  if (!visible) return null;

  const positionStyle = floating
    ? { left: floating.left, top: floating.top, right: 'auto', bottom: 'auto' }
    : cornerPosition(corner);

  return (
    <Box
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={handleDoubleClick}
      sx={{
        position: 'fixed',
        ...positionStyle,
        zIndex: 1400,
        width: { xs: 140, sm: 180, md: 220 },
        cursor: floating ? 'grabbing' : 'grab',
        touchAction: 'none',
        borderRadius: 2,
        overflow: 'visible',
        filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.35))',
        transition: floating ? 'none' : 'left 0.12s ease, top 0.12s ease, right 0.12s ease, bottom 0.12s ease'
      }}
      aria-label="Self view — drag to move, double-click to cycle corner"
    >
      <Box sx={{ position: 'relative' }}>
        {children}
        {onHide && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onHide(); }}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: 'common.white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              width: 24,
              height: 24
            }}
            aria-label="Hide self view"
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
